// §99 <positions-editor>: the entry modal's invoice-positions table (the line items of
// one invoice – each with its own amount, §10 cost bucket and §99 #7 reason).
//
// The one widget in the app whose state genuinely lives in the browser while the user
// types, so it is a Rocket component rather than server-rendered markup. It replaced an
// inline <script> that string-built rows with innerHTML, bridged to Datastar through
// two hidden bound inputs and two window.* globals, and waited on setTimeout for the
// dialog's signals to land.
//
// Data flow:
//   - The server seeds the rows through the typed `positions` prop (the entry's stored
//     positions; "[]" for a new entry) and the option lists / translated labels
//     through `labels`. A prop, not the dialog's `$positions_json` signal: the
//     element connects before Datastar applies the dialog's data-signals, which is
//     the race the old setTimeout papered over.
//   - Rows are held as FULL position objects. An edit changes only the edited field,
//     so fields the table doesn't show survive the round trip – notably the §15
//     `catalog_items` an OCR-imported line carries. The old editor rebuilt every line
//     with `catalog_items: []` on any edit, silently stripping them on save.
//   - Each edit publishes `$positions_json` (what the save handlers read) and
//     `$positions_total` (what the reconciliation line compares against `$amount`).
//     Nothing is published until the user edits, so an untouched entry saves its
//     stored positions verbatim.
//
// Light DOM, so the table keeps the app's form/table CSS. The host carries
// data-ignore-morph: the modal is rendered once when opened, and a backend morph must
// never replace the rows the user is editing. (Rocket's own re-render still runs –
// morph() only honours ignore-morph on the patched element or an ancestor of it.)

import { createCodec, rocket } from '/static/vendor/datastar-rocket.js';

type Option = { v: string; l: string };

// A position as the server stores it (model.EntryPosition). Unknown fields are kept.
type Row = {
  description: string;
  amount: number;
  kind: string;
  reason: string;
  catalog_items?: string[];
  [key: string]: unknown;
};

type Labels = {
  kinds: Option[];
  reasons: Option[];
  descPlaceholder: string;
  removeTitle: string;
  add: string;
  thDescription: string;
  thAmount: string;
  thKind: string;
  thReason: string;
};

const DEFAULT_KIND = 'werkstatt';

const parseJSON = (value: unknown): unknown => {
  if (typeof value !== 'string' || !value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

// Normalise to what the table shows: a missing kind displays (and so saves) as the
// workshop bucket, exactly as the old select did.
const toRow = (p: Record<string, unknown>): Row => ({
  ...p,
  description: str(p.description),
  amount: num(p.amount),
  kind: str(p.kind) || DEFAULT_KIND,
  reason: str(p.reason),
});

const positionsCodec = createCodec<Row[]>({
  decode(value) {
    const arr = parseJSON(value);
    if (!Array.isArray(arr)) return [];
    return arr.filter((p) => p && typeof p === 'object' && !Array.isArray(p)).map(toRow);
  },
  encode: (value) => JSON.stringify(value),
});

const options = (v: unknown): Option[] =>
  Array.isArray(v) ? v.filter((o) => o && typeof o.v === 'string').map((o) => ({ v: o.v, l: str(o.l) })) : [];

const labelsCodec = createCodec<Labels>({
  decode(value) {
    const o = (parseJSON(value) ?? {}) as Record<string, unknown>;
    return {
      kinds: options(o.kinds),
      reasons: options(o.reasons),
      descPlaceholder: str(o.descPlaceholder),
      removeTitle: str(o.removeTitle),
      add: str(o.add),
      thDescription: str(o.thDescription),
      thAmount: str(o.thAmount),
      thKind: str(o.thKind),
      thReason: str(o.thReason),
    };
  },
  encode: (value) => JSON.stringify(value),
});

// The rows, per element. Kept outside the Rocket context because `render` receives
// only props/host, and outlives a reconnect (a DOM move must not reset the edits).
const rowsByHost = new WeakMap<HTMLElement, Row[]>();

const EDITABLE = new Set(['description', 'amount', 'kind', 'reason']);

rocket('positions-editor', {
  mode: 'light',
  props: () => ({
    positions: positionsCodec,
    // Not `i18n`: Rocket maps props to kebab-case attributes and splits digits, so
    // that prop would listen on `i-18-n`.
    labels: labelsCodec,
  }),
  // Rows are seeded once; after that the component owns them.
  renderOnPropChange: false,
  setup: ({ host, props, $, action, render }) => {
    if (!rowsByHost.has(host)) rowsByHost.set(host, props.positions.map((r) => ({ ...r })));
    const rows = rowsByHost.get(host)!;

    // Blank lines (no text, no amount) are dropped, as before; everything else goes
    // out whole, including fields the table never shows.
    const publish = (): void => {
      let total = 0;
      const kept: Row[] = [];
      for (const r of rows) {
        const description = r.description.trim();
        if (!description && !r.amount) continue;
        kept.push({ ...r, description });
        total += r.amount;
      }
      $.positions_json = JSON.stringify(kept);
      $.positions_total = Math.round(total * 100) / 100;
    };

    action('setField', (_ctx, index: number, field: string, value: string) => {
      const row = rows[index];
      if (!row || !EDITABLE.has(field)) return;
      if (field === 'amount') row.amount = num(value);
      else row[field] = value;
      publish(); // no re-render: the input already shows what was typed
    });

    action('addRow', () => {
      rows.push({ description: '', amount: 0, kind: DEFAULT_KIND, reason: '' });
      render({});
      publish();
      host.querySelector<HTMLInputElement>('.pos-row:last-child .pos-desc')?.focus();
    });

    action('removeRow', (_ctx, index: number) => {
      if (index < 0 || index >= rows.length) return;
      rows.splice(index, 1);
      render({});
      publish();
    });
  },
  render: ({ html, host, props: { labels: t } }) => {
    const rows = rowsByHost.get(host) ?? [];
    const opts = (list: Option[], selected: string) =>
      list.map((o) => html`<option value="${o.v}" selected="${o.v === selected}">${o.l}</option>`);
    return html`
      <div class="table-scroll">
        <table class="ocr-lineitems-table ocr-lineitems-table--editor">
          <thead>
            <tr>
              <th>${t.thDescription}</th>
              <th class="ocr-lineitems-amount">${t.thAmount}</th>
              <th class="ocr-lineitems-kind">${t.thKind}</th>
              <th class="ocr-lineitems-kind">${t.thReason}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>${rows.map((r, i) => html`
            <tr class="pos-row">
              <td><input class="form-input pos-desc" value="${r.description}" placeholder="${t.descPlaceholder}" style="min-width:7rem" data-on:input="@setField(${i}, 'description', el.value)"/></td>
              <td class="ocr-lineitems-amount"><input class="form-input pos-amount" type="number" step="0.01" min="0" value="${r.amount}" style="width:5.5rem" data-on:input="@setField(${i}, 'amount', el.value)"/></td>
              <td class="ocr-lineitems-kind"><select class="form-input pos-kind" data-on:change="@setField(${i}, 'kind', el.value)">${opts(t.kinds, r.kind)}</select></td>
              <td class="ocr-lineitems-kind"><select class="form-input pos-reason" data-on:change="@setField(${i}, 'reason', el.value)">${opts(t.reasons, r.reason)}</select></td>
              <td><button type="button" class="btn btn--icon" style="padding:0.2rem 0.5rem;font-size:1rem" title="${t.removeTitle}" aria-label="${t.removeTitle}" data-on:click="@removeRow(${i})"><span aria-hidden="true">×</span></button></td>
            </tr>`)}
          </tbody>
        </table>
      </div>
      <button type="button" class="btn btn--secondary" style="margin-top: 0.5rem; font-size: 0.85rem; padding: 0.3rem 0.75rem;" data-on:click="@addRow()">${'+ ' + t.add}</button>
    `;
  },
});
